import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api-config';

function formatCpfDisplay(digits) {
  const d = String(digits).replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function pickToken(data) {
  if (!data || typeof data !== 'object') return '';
  const direct =
    data.token ||
    data.Token ||
    data.access_token ||
    data.accessToken ||
    data.jwt ||
    '';
  if (direct) return direct;
  const nested = data.data;
  if (nested && typeof nested === 'object') {
    return nested.token || nested.access_token || nested.jwt || '';
  }
  return '';
}

function extractUserFromLogin(data) {
  if (!data || typeof data !== 'object') return null;
  const u = data.user || data.usuario || data.cliente;
  if (u && typeof u === 'object' && !Array.isArray(u)) return u;
  const nested = data.data;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    (nested.nome || nested.cpf || nested.idCliente || nested.id)
  ) {
    return nested;
  }
  if (data.nome || data.idCliente || data.id || data.cpf) return data;
  return null;
}

async function fetchUserProfile(cpfDigits, token) {
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const r = await fetch(`${api.config.baseURL}/users/${cpfDigits}`, { headers });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function buildStoredUser(loginJson, profile, cpfDigits) {
  const u = extractUserFromLogin(loginJson) || {};
  const p = profile && typeof profile === 'object' ? profile : {};
  const nome =
    u.nome || p.nome || p.nomeCliente || p.Nome || 'Assinante';
  const id =
    u.id ??
    u.idCliente ??
    u.clienteId ??
    p.id ??
    p.idCliente ??
    p.clienteId ??
    p.IdCliente;
  const plano =
    u.plano || p.plano || p.descricaoPlano || p.Plano || '—';
  const status = u.status || p.status || p.situacao || 'Ativo';
  const vencimento =
    u.vencimento || p.vencimento || p.dataVencimento || '—';
  return {
    id,
    idCliente: id,
    clienteId: id,
    nome: String(nome),
    cpf: String(u.cpf || p.cpf || cpfDigits),
    email: String(u.email || p.email || ''),
    plano: String(plano),
    status: String(status),
    vencimento: String(vencimento),
  };
}

function Login() {
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleCpfChange = (e) => {
    setCpf(formatCpfDisplay(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: loginData, cpfDigits } = await api.loginAssinante(cpf, senha);
      const token = pickToken(loginData);
      if (token) {
        localStorage.setItem('unet_token', token);
      } else {
        localStorage.removeItem('unet_token');
      }

      const profile = await fetchUserProfile(cpfDigits, token);
      const stored = buildStoredUser(loginData, profile, cpfDigits);
      localStorage.setItem('unet_user', JSON.stringify(stored));

      navigate('/central');
    } catch (err) {
      setError(err?.message || 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="login-container">
          <div className="login-header">
            <h1>Central do Assinante</h1>
            <p>Faça login com seu CPF e senha</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="cpf">CPF</label>
              <input
                type="text"
                id="cpf"
                name="cpf"
                value={cpf}
                onChange={handleCpfChange}
                required
                autoComplete="username"
                inputMode="numeric"
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>

            <div className="form-group">
              <label htmlFor="senha">Senha</label>
              <input
                type="password"
                id="senha"
                name="senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Sua senha"
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Não tem uma conta? <Link to="/cadastro">Cadastre-se</Link>
            </p>
            <p>
              <Link to="/suporte">Esqueci minha senha</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
